import { useController } from "react-hook-form";

import SegmentedToggleInput from "@/form/inputs/SegmentedToggleInput/SegmentedToggleInput";
import { useFieldIds } from "@/form/useFieldIds";
import { cn } from "@/lib/utils";

import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

export type SegmentedToggleFieldProps = {
  readonly disabled?: boolean;
  readonly name: string;
  readonly label: string;
  readonly options: { value: string; label: string }[];
  readonly size?: "sm" | "md" | "lg";
  readonly className?: string;
  readonly hint?: string;
  readonly hideValidation?: boolean;
  readonly hideLabel?: boolean;
  readonly "data-testid"?: string;
};

export default function SegmentedToggleField({
  disabled = false,
  label,
  name,
  options,
  size = "sm",
  className,
  hint,
  hideLabel = false,
  hideValidation = false,
  "data-testid": dataTestId,
}: SegmentedToggleFieldProps): React.ReactElement {
  const { inputId, labelId, hintId, errorId, describedBy } = useFieldIds(name);

  const { field, fieldState } = useController({
    name,
  });

  const invalid = !!fieldState.error;

  const handleChange = (value: string) => {
    field.onChange(options.find((option) => option.value === value));
  };

  return (
    <div
      className={cn("SegmentedToggleField grid gap-2")}
      data-testid={dataTestId}
      data-field={name}
    >
      <div className={cn("grid", hideLabel && "sr-only")}>
        <FieldLabel
          htmlFor={inputId}
          id={labelId}
          invalid={invalid}
          value={label}
        />
      </div>

      <SegmentedToggleInput
        id={inputId}
        name={name}
        options={options}
        value={field.value?.value}
        onChange={handleChange}
        size={size}
        disabled={disabled}
        className={className}
        aria-describedby={describedBy(!!hint, !!fieldState.error)}
        aria-invalid={invalid}
      />

      <div
        className={cn(
          "align-center flex h-3 justify-start",
          hideValidation && "hidden"
        )}
      >
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}
